import { useCallback, useState } from 'react';
import log from 'loglevel';
import qs from 'query-string';
import useFetch from 'hooks/useFetch';
import { NonClientError } from 'hooks/requests/fetchUtils';
import { map, filter, curry } from 'ramda';
import catchOnly from 'utils/catchOnly';

const MELISSA_URL =
  'https://expressentry.melissadata.net/web/GlobalExpressFreeForm';
const LICENSE_KEY = 'lt9OthVnlCRomUk-DNvwl9**';

export interface AddressResult {
  formattedAddress: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
}

type AddressAutocompleteHook = () => [
  AddressResult[],
  (query: string, partialAddress: Partial<AddressResult>) => Promise<void>,
];

/**
 * Resposne type from MelissaData
 * See GlobalExpressFreeForm section in
 * http://wiki.melissadata.com/images/c/cc/GlobalExpressEntry_QSG.pdf
 */
interface MelissaResponse {
  Results: {
    Address: {
      Address: string;
      Address1: string;
      Locality: string;
      AdministrativeArea: string;
      PostalCode: string;
    };
  }[];
}

const convertResult = (body: MelissaResponse): AddressResult[] => {
  const results = body.Results || [];

  return map(
    result => ({
      formattedAddress: result.Address.Address,
      address1: result.Address.Address1,
      city: result.Address.Locality,
      state: result.Address.AdministrativeArea,
      zip: result.Address.PostalCode,
    }),
    results,
  );
};

const filterResults = curry(
  (
    partialAddress: Partial<AddressResult>,
    results: AddressResult[],
  ): AddressResult[] => {
    return filter(
      addressResult =>
        !partialAddress.zip ||
        addressResult.zip.substr(0, 5) === partialAddress.zip,
      results,
    );
  },
);

const useAddressAutocomplete: AddressAutocompleteHook = () => {
  const [results, setResults] = useState<AddressResult[]>([]);
  const [, fetch] = useFetch();

  const search = useCallback(
    async (query: string, partialAddress: Partial<AddressResult> = {}) => {
      const url = new URL(MELISSA_URL);
      url.search = qs.stringify({
        format: 'JSON',
        id: LICENSE_KEY,
        ff: query,
        maxrecords: 8,
        nativecharset: false,
      });

      try {
        const response = await fetch(url.toString(), {
          method: 'GET',
          mode: undefined,
          headers: undefined,
        });

        if (!response.ok) {
          log.error('Failed to fetch address suggestion');
          return;
        }

        const body = (await response.json()) as MelissaResponse;
        setResults(filterResults(partialAddress, convertResult(body)));
        return;
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          return;
        }
      }
    },
    [fetch, setResults],
  );

  return [results, search];
};

export default useAddressAutocomplete;
